import { forwardRef, useContext } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Range as SlateRange } from 'slate';
import { Editable } from 'slate-react';

import { useIsTranslated } from 'src/lib/hooks';

import { useInitSelection } from '../../lib/useInitSelection';
import { CustomElement } from '../CustomElement';
import { CustomSlateContext } from '../CustomSlate';
import { LinkEditPopup } from '../LinkEditPopup';
import { LinkViewPopover } from '../LinkViewPopover';
import { TextLeaf } from '../TextLeaf';

import { usePropagateFocusChange } from './usePropagateFocusChange';
import { useStyles } from './useStyles';

const defaultRenderElement = (props) => <CustomElement {...props} />;
const defaultRenderLeaf = (props) => <TextLeaf {...props} />;

const propTypes = {
  className: PropTypes.string,
  renderElement: PropTypes.func,
  renderLeaf: PropTypes.func,
  readOnly: PropTypes.bool,
  onFocusChange: PropTypes.func,
};

const defaultProps = {
  className: '',
  renderElement: null,
  renderLeaf: null,
  readOnly: false,
  onFocusChange: null,
};

export const CustomEditable = forwardRef(
  ({ className, renderElement, renderLeaf, readOnly, onFocusChange, ...restProps }, rootRef) => {
    const styles = useStyles();
    const isTranslated = useIsTranslated();
    const { isViewLinkOpen, closeViewLinkPopup, isEditLinkOpen, closeEditLinkPopup } =
      useContext(CustomSlateContext);

    useInitSelection();
    usePropagateFocusChange(onFocusChange);

    return (
      <>
        <Editable
          className={cx(styles.root, className)}
          ref={rootRef}
          renderElement={renderElement || defaultRenderElement}
          renderLeaf={renderLeaf || defaultRenderLeaf}
          readOnly={isTranslated || readOnly}
          scrollSelectionIntoView={scrollSelectionIntoView}
          {...restProps}
        />

        {isViewLinkOpen && <LinkViewPopover onClose={closeViewLinkPopup} />}
        {isEditLinkOpen && <LinkEditPopup onClose={closeEditLinkPopup} />}
      </>
    );
  }
);

CustomEditable.displayName = 'CustomEditable';
CustomEditable.propTypes = propTypes;
CustomEditable.defaultProps = defaultProps;

// This is almost the same implementation as the default one in slate-react, but with native `scrollIntoView` + "smooth" behavior
// See: node_modules/slate-react/dist/index.js -> defaultScrollSelectionIntoView
const scrollSelectionIntoView = (editor, domRange) => {
  if (!domRange.getBoundingClientRect) {
    return;
  }

  // This was affecting the selection of multiple blocks and dragging behavior,
  // so enabled only if the selection has been collapsed.
  if (!editor.selection || (editor.selection && SlateRange.isCollapsed(editor.selection))) {
    const leafEl = domRange.startContainer.parentElement;
    leafEl.getBoundingClientRect = domRange.getBoundingClientRect.bind(domRange);
    try {
      leafEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    } finally {
      delete leafEl.getBoundingClientRect;
    }
  }
};
